import type { SdkConfig } from '../core/types';
import type { Api } from '../core/api';
import { throttle, nowMs } from '../core/utils';
import { redactUrl } from '../core/redact';
import { log } from '../core/log';

/**
 * Heatmap-grade capture — opt-in via `autoTrack.heatmap`.
 *
 * Emits three event families, all with viewport-aware coords so the
 * backend can re-project them onto screenshots of the page taken later:
 *
 *   - heatmap.click        single event per click with element selector +
 *                          absolute coords + viewport size
 *   - heatmap.mouse_trail  batched mousemove samples (~1 sample/200ms,
 *                          flushed every 5s or 50 points)
 *   - heatmap.scroll       granular scroll depth (~every 5%) with viewport
 *                          height + total scroll height for normalization
 *
 * Off by default: mouse trails produce ~5x the volume of the rest of
 * auto-tracking combined. The integrator turns it on when they have a
 * backend that can swallow it (ClickHouse / S3 parquet / BigQuery).
 *
 * Privacy posture: we never capture target.value or innerText of inputs.
 * The element selector + class/id only. Sensitive zones (passwords,
 * `data-emblue-private`) are stripped from selectors.
 */

const MOUSE_SAMPLE_MS = 200;
const MOUSE_FLUSH_MS = 5000;
const MOUSE_FLUSH_POINTS = 50;
const SCROLL_BUCKET_PCT = 5;
const MAX_SELECTOR_LEN = 200;

interface MousePoint {
	x: number;
	y: number;
	/** Document coords, sampled at this point's own scroll offset — the
	 *  message-level scroll_x/scroll_y is the offset at flush time, which can
	 *  be thousands of px away from where an early point actually was. */
	page_x: number;
	page_y: number;
	t: number; // ms since trail start
}

interface ViewportInfo {
	viewport_w: number;
	viewport_h: number;
	scroll_x: number;
	scroll_y: number;
}

function viewportInfo(): ViewportInfo {
	const doc = document.documentElement;
	return {
		viewport_w: window.innerWidth || doc.clientWidth || 0,
		viewport_h: window.innerHeight || doc.clientHeight || 0,
		scroll_x: window.pageXOffset || doc.scrollLeft || 0,
		scroll_y: window.pageYOffset || doc.scrollTop || 0,
	};
}

/** Total document height in CSS px. Needed on every event that carries
 *  document coords so the backend can normalize positions across sessions
 *  that saw pages of different lengths. */
function docHeight(): number {
	const doc = document.documentElement;
	const body = document.body;
	return Math.max(doc ? doc.scrollHeight : 0, body ? body.scrollHeight : 0);
}

/** Element → short CSS selector. Bias toward id, then unique class, then
 *  tag + nth-child path. Capped at MAX_SELECTOR_LEN. */
function selectorFor(el: Element | null): string {
	if (!el || el.nodeType !== 1) return '';
	if (el === document.documentElement) return 'html';
	if (el === document.body) return 'body';
	if (el.hasAttribute('data-emblue-private')) return '<private>';

	if (el.id) return `#${cssEscape(el.id)}`;

	const parts: string[] = [];
	let cur: Element | null = el;
	while (cur && cur.nodeType === 1 && parts.length < 6) {
		if (cur === document.body) {
			parts.unshift('body');
			break;
		}
		const tag = cur.tagName.toLowerCase();
		if (cur.id) {
			parts.unshift(`#${cssEscape(cur.id)}`);
			break;
		}
		const cls = (cur.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
		const classPart = cls.length ? '.' + cls.map(cssEscape).join('.') : '';
		// Add nth-child only when needed to disambiguate among siblings of the same tag.
		const parent: Element | null = cur.parentElement;
		const self: Element = cur;
		if (parent) {
			const sameTag = (Array.prototype.filter.call(parent.children, (c: Element) => c.tagName === self.tagName) as Element[]);
			if (sameTag.length > 1) {
				const idx = sameTag.indexOf(self) + 1;
				parts.unshift(`${tag}${classPart}:nth-of-type(${idx})`);
			} else {
				parts.unshift(`${tag}${classPart}`);
			}
		} else {
			parts.unshift(`${tag}${classPart}`);
		}
		cur = parent;
	}
	const sel = parts.join(' > ');
	return sel.length > MAX_SELECTOR_LEN ? sel.slice(0, MAX_SELECTOR_LEN) : sel;
}

function cssEscape(s: string): string {
	// Minimal CSS-ident escaper. CSS.escape would be ideal but Safari < 14 lacks it.
	return s.replace(/[^a-zA-Z0-9_-]/g, '_');
}

export function installHeatmap(config: SdkConfig, emblue: Api): void {
	if (!config.autoTrack.heatmap) return;
	if (typeof document === 'undefined' || typeof window === 'undefined') return;

	// Heatmap fires clicks/scroll buckets/mouse-trails fast enough to hit the
	// default batch.maxEvents=20 in <2s, producing dozens of HTTP requests
	// per session. Bump the buffer ceilings so events accumulate into bigger,
	// less frequent flushes. Math.max preserves any explicit init() override
	// that was already higher.
	config.batch.maxEvents = Math.max(config.batch.maxEvents, 100);
	config.batch.maxWaitMs = Math.max(config.batch.maxWaitMs, 5000);
	config.batch.maxBytes = Math.max(config.batch.maxBytes, 64 * 1024);

	log('heatmap: installed', `(batch=${config.batch.maxEvents}/${config.batch.maxWaitMs}ms/${config.batch.maxBytes}B)`);

	// ────────── Click capture ──────────
	document.addEventListener(
		'click',
		(e: MouseEvent) => {
			const target = e.target as Element | null;
			const v = viewportInfo();
			emblue.track('heatmap.click' as any, {
				x: e.clientX,
				y: e.clientY,
				page_x: e.clientX + v.scroll_x,
				page_y: e.clientY + v.scroll_y,
				selector: selectorFor(target),
				button: e.button,
				url: redactUrl(location.href),
				doc_height: docHeight(),
				...v,
			});
		},
		true,
	);

	// ────────── Mouse trail (batched) ──────────
	let trail: MousePoint[] = [];
	let trailStart = 0;
	let flushTimer: ReturnType<typeof setTimeout> | null = null;

	const flushTrail = () => {
		if (flushTimer) {
			clearTimeout(flushTimer);
			flushTimer = null;
		}
		if (trail.length === 0) return;
		const v = viewportInfo();
		emblue.track('heatmap.mouse_trail' as any, {
			points: trail,
			started_at: trailStart,
			point_count: trail.length,
			url: redactUrl(location.href),
			...v,
		});
		trail = [];
		trailStart = 0;
	};

	const sample = throttle((e: MouseEvent) => {
		const now = nowMs();
		if (trail.length === 0) trailStart = now;
		const v = viewportInfo();
		trail.push({
			x: e.clientX,
			y: e.clientY,
			page_x: e.clientX + v.scroll_x,
			page_y: e.clientY + v.scroll_y,
			t: now - trailStart,
		});
		if (trail.length >= MOUSE_FLUSH_POINTS) {
			flushTrail();
		} else if (!flushTimer) {
			flushTimer = setTimeout(flushTrail, MOUSE_FLUSH_MS);
		}
	}, MOUSE_SAMPLE_MS);

	document.addEventListener('mousemove', sample, { passive: true });
	window.addEventListener('pagehide', flushTrail);

	// ────────── Granular scroll buckets ──────────
	let lastBucket = -1;
	const scrollSample = throttle(() => {
		const v = viewportInfo();
		const height = docHeight();
		const scrollable = height - v.viewport_h;
		if (scrollable <= 0) return;
		const pct = Math.floor((v.scroll_y / scrollable) * 100);
		const bucket = Math.floor(pct / SCROLL_BUCKET_PCT) * SCROLL_BUCKET_PCT;
		if (bucket === lastBucket) return;
		lastBucket = bucket;
		emblue.track('heatmap.scroll' as any, {
			percent: pct,
			bucket,
			doc_height: height,
			url: redactUrl(location.href),
			...v,
		});
	}, 200);

	window.addEventListener('scroll', scrollSample, { passive: true });
}
